import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRide } from '../contexts/RideContext';
import { Navigation, Pause, Play, Square, MapPin } from 'lucide-react';

const RideFloatingIndicator = () => {
  const ride = useRide();
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState('00:00');

  useEffect(() => {
    if (!ride?.rideActive || !ride.rideStartTime) return;
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(ride.rideStartTime).getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [ride?.rideActive, ride?.rideStartTime]);

  if (!ride?.rideActive) return null;

  // Don't show on map page (it has its own controls)
  if (window.location.pathname === '/' || window.location.pathname === '/kort') return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-bottom-4 duration-300"
      style={{ bottom: 'calc(60px + env(safe-area-inset-bottom, 0px))' }}
      data-testid="ride-floating-indicator"
    >
      <div
        className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl shadow-2xl cursor-pointer"
        style={{
          background: 'rgba(16,16,16,0.95)',
          border: ride.ridePaused ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(16,185,129,0.4)',
          backdropFilter: 'blur(16px)',
          boxShadow: ride.ridePaused
            ? '0 4px 24px rgba(245,158,11,0.15)'
            : '0 4px 24px rgba(16,185,129,0.15)',
        }}
        onClick={() => navigate('/kort')}
      >
        {/* Pulse dot */}
        <div className="relative flex-shrink-0">
          {!ride.ridePaused && (
            <div className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-400 animate-ping opacity-30" />
          )}
          <div className={`w-3 h-3 rounded-full ${ride.ridePaused ? 'bg-amber-500' : 'bg-emerald-400'}`} />
        </div>

        {/* Distance */}
        <div className="flex items-baseline gap-1">
          <span className="text-lg font-black text-white tabular-nums">{ride.rideDistance.toFixed(1)}</span>
          <span className="text-[10px] text-zinc-500 font-medium">km</span>
        </div>

        {/* Divider */}
        <div className="w-px h-5 bg-white/10" />

        {/* Live points */}
        <div className="flex items-baseline gap-0.5">
          <span className="text-sm font-bold text-amber-400 tabular-nums">{(ride.rideDistance / 100 * 2).toFixed(1)}</span>
          <span className="text-[9px] text-amber-400/50 font-medium">p</span>
        </div>

        {/* Divider */}
        <div className="w-px h-5 bg-white/10" />

        {/* Time */}
        <span className="text-xs font-mono text-zinc-400 tabular-nums">{elapsed}</span>

        {/* Divider */}
        <div className="w-px h-5 bg-white/10" />

        {/* Quick controls */}
        <div className="flex items-center gap-1">
          {!ride.ridePaused ? (
            <button
              onClick={(e) => { e.stopPropagation(); ride.pauseRide(); }}
              className="w-7 h-7 rounded-lg flex items-center justify-center bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
              data-testid="float-pause-btn"
            >
              <Pause className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); ride.resumeRide(); }}
              className="w-7 h-7 rounded-lg flex items-center justify-center bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
              data-testid="float-resume-btn"
            >
              <Play className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); ride.endRide(); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            data-testid="float-stop-btn"
          >
            <Square className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Map link hint */}
        <MapPin className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
      </div>
    </div>
  );
};

export default RideFloatingIndicator;
